import Bus from '../../bus'
import { compact, get, intersection, isArray, isEmpty, isFunction, map, size } from 'lodash'

import { emptyBetslipState } from './betslip.constants'

import {
  areBetGroupsDisintersected,
  extractBestlipOutcomesErrors,
  getBetEventPartials,
  getBetItemsForMulti,
  getBetsHash,
  getIntUID,
  insertInArrayByIndex,
  logger,
  mergeUpdate,
  removeFromCollection,
  removeInArrayByIndex
} from '../../utils'

import {
  BETSLIP_ADD_BET,
  BETSLIP_CLEAR_BETS,
  BETSLIP_CLEAR_BETS_OR_BET_NOW,
  BETSLIP_CLEAR_ERROR,
  BETSLIP_REMOVE_BET,
  BETSLIP_REMOVE_BET_NOW,
  BETSLIP_SELECT_BET,
  BETSLIP_SETTINGS_UPDATE,
  BETSLIP_STATE_UPDATE,
  BETSLIP_UPDATE_BET,
  BETSLIP_UPDATE_BET_TYPE,
  BETSLIP_UPDATE_BETS,
  BETSLIP_UPDATE_ERRORS
} from '../../actions'

import { BET_EVENT_TYPE, BET_TYPE, BETSLIP_VALIDATION_STATUS } from '../../constants'

import BetManager from '../../managers/bet'
import { isExoticMarket, isMultilegsMarket } from '../../index'

/*
  Recounting action handlers map
  After changing bet items requires recalculations,
  status checks and validations
*/
const betslipRecountingActionHandlersMap = {
  // Handling adding new bet (without toggle remove or autoreplace)
  [BETSLIP_ADD_BET]: (state, action) => {
    // Blocking from add/remove bets once betslip loading
    // Returning no changes
    if (state.loading) {
      return {}
    }

    const bets = state.betItems
    const bet = action.payload
    const requestId = bet.requestId || getIntUID()

    const sameBetExist = bets.some(
      (value) =>
        (value.eventId === bet.eventId && // If event id the same
          !areBetGroupsDisintersected(value.betGroups, bet.betGroups)) || // With the same bet group
        value.outcomeId === bet.outcomeId // Or if outcome ids the same
    )

    // Return no updates
    if (sameBetExist) {
      return {}
    }

    // Adding new bet
    // Case if bet.event is empty
    if (isEmpty(bet.event)) {
      return {
        betItems: [
          ...bets,
          {
            ...bet,
            requestId,
            synchronized: false
          }
        ]
      }
    }

    const { odds, suspended, active, oddsForMulti } = getBetEventPartials(bet.event, {
      marketTypeId: bet.marketTypeId,
      marketId: bet.marketId,
      outcomeId: bet.outcomeId,
      betEventType: bet.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH,
      isBespokeOutcome: bet.isBespokeOutcome
    })

    // Initializing bet with main partials
    return {
      betItems: [
        ...bets,
        {
          ...bet,
          synchronized: true,
          requestId,
          // Adding extra bet partials
          odds,
          oddsForMulti,
          suspended,
          active
        }
      ]
    }
  },

  // Handling selecting new bet (add/auto-replace/remove)
  [BETSLIP_SELECT_BET]: (state, action) => {
    // Blocking from add/remove bets once betslip loading
    // Returning no changes
    if (state.loading) {
      return {}
    }

    const bets = state.betItems.filter(bet => !bet.betNow) // remove other betNow items
    const bet = action.payload

    // This probably should not be here... But entire Betslip needs rebuild.
    if (!bets || !bets.length) {
      Bus.emit(Bus.events.racing.clearBetslipRunners)
    }

    const index = bets.findIndex((value) => {
      return !isMultilegsMarket(value.marketTypeId) && !isExoticMarket(value.marketTypeId) && value.outcomeId === bet.outcomeId

      /* // Searching for bet with the same event.id and bet group

       if (bet.betEventType === BET_EVENT_TYPE.RACING) {
         return value.outcomeId === bet.outcomeId
       } else {
         return (
           value.eventId === bet.eventId &&
           // And checking bet groups intersection
           // For not provided bet groups - bet will always be replaced in same event
           !areBetGroupsDisintersected(value.betGroups, bet.betGroups)
         )
       } */
    })

    // First, checking if same bet not exist
    if (index < 0) {
      const requestId = bet.requestId || getIntUID()

      // Adding new bet case
      // If event in bet not provided
      if (isEmpty(bet.event)) {
        // Adding new bet
        return {
          confirming: false,
          betItems: [
            ...bets,
            {
              ...bet,
              requestId,
              synchronized: false
            }
          ]
        }
      }

      // If bet event provided
      // Getting main event partials
      const { odds, suspended, active, oddsForMulti } = getBetEventPartials(bet.event, {
        marketTypeId: bet.marketTypeId,
        marketId: bet.marketId,
        outcomeId: bet.outcomeId,
        betEventType: bet.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH,
        isBespokeOutcome: bet.isBespokeOutcome
      })

      // Also should not be here
      if (bet.event?.runners) {
        Bus.emit(Bus.events.racing.addBetslipRunners, bet)
      }

      // Adding bet with main partials
      return {
        confirming: false,
        betItems: [
          ...bets,
          {
            ...bet,
            synchronized: true,
            requestId,
            // Adding extra bet partials
            odds,
            oddsForMulti,
            suspended,
            active
          }
        ]
      }
    }

    // Outcome exist. Checking if they are the same
    // If two conditions are true - that is the same bet, removing
    const { outcomeId, requestId } = bets[index]

    if (outcomeId === bet.outcomeId) {
      return {
        confirming: false,
        betItems: removeInArrayByIndex(bets, index)
      }
    }

    // Outcomes are different
    // Applying autoreplace update

    // Case if updating bet does not contain event data
    if (isEmpty(bet.event)) {
      return {
        confirming: false,
        betItems: insertInArrayByIndex(bets, index, {
          ...bet,
          requestId: requestId || getIntUID(),
          synchronized: false
        })
      }
    }

    // Case if updating bet contain event data
    // Resolving bet event partials
    const { odds, suspended, active, oddsForMulti } = getBetEventPartials(bet.event, {
      marketTypeId: bet.marketTypeId,
      marketId: bet.marketId,
      outcomeId: bet.outcomeId,
      betEventType: bet.betEventType || BET_EVENT_TYPE.SPORT_PREMATCH,
      isBespokeOutcome: bet.isBespokeOutcome
    })

    return {
      confirming: false,
      betItems: insertInArrayByIndex(bets, index, {
        ...bet,
        requestId: requestId || getIntUID(),
        synchronized: true,
        odds,
        oddsForMulti,
        suspended,
        active
      })
    }
  },

  [BETSLIP_REMOVE_BET]: (state, action) => {
    // Blocking from add/remove bets once betslip loading
    // Returning no changes
    if (state.loading) {
      return {}
    }

    const bets = state.betItems
    const bet = action.payload

    const filteredBetItems = bets.filter(
      (value) => value.requestId !== bet.requestId
    )

    const filteredBonusAssignments = (state.bonusAssignments || []).filter(ba => {
      return filteredBetItems.some(bet => bet.requestId === ba.requestId)
    })

    return {
      betItems: filteredBetItems,
      bonusAssignments: filteredBonusAssignments,
      limits: {
        ...state.limits,
        [bet.requestId]: undefined,
      }
    }
  },

  [BETSLIP_REMOVE_BET_NOW]: (state, action) => {
    return { betItems: state.betItems?.filter(bet => !bet.betNow) }
  },
  [BETSLIP_UPDATE_BET]: (state, action) => {
    // Seaching bet index with provided request id
    const index = state.betItems.findIndex(
      (value) => value.requestId === action.payload.requestId
    )

    // If not exist
    if (index < 0) {
      // Nothing changes
      return {}
    }

    const oldBetItem = state.betItems[index];
    // Reset limits of bet item if outcome changed
    const limits = action.payload.outcomeId === oldBetItem.outcomeId ? state.limits : {
      ...state.limits,
      [action.payload.requestId]: undefined
    };

    // Updating bet by index
    return {
      limits: limits,
      betItems: insertInArrayByIndex(state.betItems, index, {
        ...oldBetItem,
        ...action.payload
      })
    }
  },
  [BETSLIP_UPDATE_BETS]: (state, action) => {
    const { requestIds, ...payload } = action.payload
    const requestIdsArr = isArray(requestIds) ? requestIds : [requestIds]
    // update all or by list of request ids
    return {
      betItems: state.betItems.map(betItem => {
        return isEmpty(requestIdsArr)
          ? ({ ...betItem, ...payload }) // update all
          : requestIdsArr.includes(betItem.requestId) // update if in the list
            ? ({ ...betItem, ...payload })
            : ({ ...betItem })
      })
    }
  },
  [BETSLIP_CLEAR_BETS]: (state, action) => {
    return {
      betItems: emptyBetslipState.betItems,
      betTypes: emptyBetslipState.betTypes,
      bonusAssignments: emptyBetslipState.bonusAssignments,
      limits: emptyBetslipState.limits,
      errors: emptyBetslipState.errors,
      confirming: false,
      outcomesErrors: emptyBetslipState.outcomesErrors
    }
  },
  [BETSLIP_CLEAR_BETS_OR_BET_NOW]: (state, action) => {
    const betNow = state.betItems?.find(bet => bet.betNow)
    if (betNow) {
      return { betItems: state.betItems?.filter(bet => !bet.betNow) }
    } else {
      return {
        betItems: emptyBetslipState.betItems,
        betTypes: emptyBetslipState.betTypes,
        bonusAssignments: emptyBetslipState.bonusAssignments,
        limits: emptyBetslipState.limits,
        errors: emptyBetslipState.errors,
        confirming: false,
        outcomesErrors: emptyBetslipState.outcomesErrors
      }
    }
  }
}

/*
  Default action handlers
*/
const betslipActionHandlersMap = {
  [BETSLIP_UPDATE_BET_TYPE]: (state, action) => {
    const { betTypes } = state

    const index = betTypes.findIndex((value) => value.id === action.payload.id)

    if (index < 0) {
      // Nothing changes
      return state
    }

    return {
      ...state,
      betTypes: insertInArrayByIndex(betTypes, index, {
        ...state.betTypes[index],
        ...action.payload
      })
    }
  },
  [BETSLIP_UPDATE_ERRORS]: (state, action) => {
    const initialErrors = action.payload
    const outcomeIdsInBetslip = map(state.betItems, ({ outcomeId }) =>
      String(outcomeId || '')
    )

    // will filter out all errors which a related to removed outcomes from betslip
    const errors = compact(
      map(initialErrors, (error) => {
        const violations = get(error, 'violations', [])

        // get array of outcome ids from violations
        const relatedOutcomeIds = compact(
          map(violations, (violation) =>
            String(get(violation, 'violationInfo.outcomeId') || '')
          )
        )

        if (
          // return error if error is global
          !size(relatedOutcomeIds) ||
          // return error if the error is still valid(has related outcome in betslip)
          size(intersection(outcomeIdsInBetslip, relatedOutcomeIds))
        ) {
          return error
        }
      })
    )

    const outcomesErrors = extractBestlipOutcomesErrors(errors)

    return {
      ...state,
      errors,
      outcomesErrors,
      loading: false,
      validationStatus: BETSLIP_VALIDATION_STATUS.VALIDATION_FAILED
    }
  },
  [BETSLIP_CLEAR_ERROR]: (state, action) => {
    // Skip if error with provided requestId not exist
    if (!state.errors[action.payload]) {
      return state
    }

    const errors = removeFromCollection(state.errors, action.payload)

    const outcomesErrors = extractBestlipOutcomesErrors(errors)

    return {
      ...state,
      errors,
      outcomesErrors
    }
  },
  [BETSLIP_SETTINGS_UPDATE]: (state, action) => {
    return {
      ...state,
      settings: mergeUpdate(state.settings, action.payload)
    }
  }
}

/*
  Betslip reducer
*/

export const betslipReducer = (state, action) => {
  logger.debug(`[betslip:${state.id}] dispatching action`, action)

  if (action.type === BETSLIP_STATE_UPDATE) {
    return {
      ...state,
      ...action.payload
    }
  }

  const recountingActionReducer = betslipRecountingActionHandlersMap[action.type]

  /*
    If its recounting action
    Applying extra checks
  */
  if (isFunction(recountingActionReducer)) {
    const updatedState = {
      ...state,
      ...recountingActionReducer(state, action)
    }

    // Case when in updated state bets exist
    if (updatedState.betItems.length > 0) {
      // Recounting global bets states
      updatedState.active = updatedState.betItems.every((bet) => bet.active)
      updatedState.suspended = updatedState.betItems.some(
        (bet) => bet.suspended
      )
      const betsWithOdds = getBetItemsForMulti(updatedState.betItems) || []
      updatedState.fullfited = betsWithOdds.every((bet) => bet.odds)
    } else {
      // Handling empty bets state
      // Resetting global statuses
      updatedState.active = emptyBetslipState.active
      updatedState.suspended = emptyBetslipState.suspended
      updatedState.fullfited = emptyBetslipState.fullfited

      // Resetting other betslip state parts
      updatedState.betshash = emptyBetslipState.betshash
      updatedState.betTypes = emptyBetslipState.betTypes
      updatedState.bonuses = emptyBetslipState.bonuses
      updatedState.errors = emptyBetslipState.errors
      updatedState.outcomesErrors = emptyBetslipState.outcomesErrors
      updatedState.validationStatus = emptyBetslipState.validationStatus
      updatedState.limits = emptyBetslipState.limits

      // Broadcasting snapshot (no bets, no additions)
      Bus.emit(`betslip:${state.id}:${Bus.events.bet.snapshot}`, {
        betItems: emptyBetslipState.betItems,
        bonusAssignments: emptyBetslipState.bonusAssignments,
        limits: emptyBetslipState.limits,
        addition: false
      })

      return updatedState
    }

    /*
      Determine if changes have occurred
      Сhanges in the outcomes or in odds most likely change the statuses
      recounting if only betslip initialized
      - broadcasting snapshot
      - recounting bet types
      - generating bets hash
      - updating validation status
    */

    // Generating new betshash
    const updatedBetshash = getBetsHash(updatedState.betItems)

    // Once betslip initialized and bet items have been changed
    if (state.betshash !== updatedBetshash && updatedState.initialized) {
      /*
        Based on the current bets, we form avalidable bet types
        using betslip bet types settings
      */
      const availableBetTypes =
        BetManager.betTypesCalculator.getAvailableBetTypes(updatedState)

      /*
        Merging available bet types into existing
      */
      updatedState.betTypes =
        BetManager.betTypesCalculator.mergeAvailableBetTypes(
          availableBetTypes,
          updatedState.betTypes
        )

      if (!updatedState.betTypes.find(betType => betType.betType === updatedState.selectedBetType)) {
        updatedState.selectedBetType = updatedState.betTypes[1]?.betType || BET_TYPE.MULTIPLE
      }

      logger.debug(
        `[betslip:${state.id}] recalculating bet types`,
        updatedState.betTypes
      )

      // Checking if bet outcomes been changed
      // Updating state betshash
      updatedState.betshash = updatedBetshash

      // Resetting errors
      updatedState.errors = emptyBetslipState.errors
      updatedState.outcomesErrors = emptyBetslipState.outcomesErrors

      // Resetting bonuses
      updatedState.bonuses = emptyBetslipState.bonuses
      // updatedState.bonusAssignments = emptyBetslipState.bonusAssignments

      // Setting validation status as waiting for server validation
      updatedState.validationStatus =
        BETSLIP_VALIDATION_STATUS.WAITING_VALIDATION

      // Broadcasting snapshot
      Bus.emit(`betslip:${state.id}:${Bus.events.bet.snapshot}`, {
        betItems: updatedState.betItems,
        addition: updatedState.betItems.length > state.betItems.length
      })
    }

    return updatedState
  }

  /*
    Default actions handler
  */
  const actionReducer = betslipActionHandlersMap[action.type]

  if (isFunction(actionReducer)) {
    return actionReducer(state, action)
  }

  return state
}
